/**
 * CounterSettings Component
 * 
 * This component provides a settings interface for managing a counter, including:
 * - Editing counter name and visibility
 * - Setting or modifying time limits (deadline or countdown)
 * - Managing counter data (resetting counts, deleting buttons)
 * - Deleting the counter
 * 
 * The component features a real-time time display for counters with time limits,
 * which updates every second without requiring constant data refreshes.
 * 
 * Key features:
 * - Form-based editing of counter properties
 * - Real-time countdown display with efficient updates
 * - Modal confirmations for destructive actions
 * - Access control based on user permissions
 */
import * as React from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { Modal } from 'bootstrap';
import { resetButtonCounts, deleteAllButtons } from '../../services/api';
import { useToast } from '../../utils/toastUtils';
import ToastContainer from '../common/ToastContainer';
import { useCounters } from '../../contexts/CounterContext';
import { useUser } from '../../contexts/UserContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { config } from '../../config';
import { formatDateWithTimezone, getUserTimezone, normalizeInputDatetime } from '../../utils/dateUtils';
import { isOwnerUrlIdentifier, findCounterByIdentifier } from '../../utils/urlUtils';
import LoadingSpinner from '../common/LoadingSpinner';

function CounterSettings() {
  const { t } = useTranslation();
  const { toasts, showToast } = useToast();
  const { counterId } = useParams<{ counterId?: string }>();
  const navigate = useNavigate();
  const { user, isAdmin, hasCounterAccess } = useUser();
  const userEmail = user.email;
  const { 
    selectedCounterId, 
    selectCounter, 
    refreshCounters: contextRefreshCounters, 
    counters, 
    updateCounterDetails,
    removeCounter,
    findCounterByOwnerUrl,
    loading: countersLoading
  } = useCounters();
  
  // State hooks
  const [actualCounterId, setActualCounterId] = useState<string | undefined>(counterId);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingTimeLimit, setIsEditingTimeLimit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [counterName, setCounterName] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [timeLimitType, setTimeLimitType] = useState<'deadline' | 'countdown' | null>(null);
  const [deadline, setDeadline] = useState('');
  const [countdownMinutes, setCountdownMinutes] = useState<number>(60);
  const [countdownStartedAt, setCountdownStartedAt] = useState<string | null>(null);
  const [timezone, setTimezone] = useState(getUserTimezone());
  const [timeUpdateTrigger, setTimeUpdateTrigger] = useState(0);
  const [wasExpired, setWasExpired] = useState(false);
  
  // Remove polling with silent updates - now we'll use the timer for time display
  // and explicit refreshes for data
  const refreshCounters = contextRefreshCounters;
  
  // Derived values (non-hook calculations)
  const counterIdentifier = actualCounterId || counterId;
  const counter = counters.find(c => c._id === counterId || c.ownerUrl === counterId);
  const isOwner = counter && userEmail ? counter.ownerEmail === userEmail : false;
  
  // Use our consistent util functions to determine URL type
  const isOwnerUrl = counterId ? isOwnerUrlIdentifier(counterId, counters) : false;
  
  // Use central access control logic instead of local check
  const hasManagementRights = counterId && counter
    ? hasCounterAccess(counter.ownerEmail || null, isOwnerUrl, 'manage')
    : false;
    
  const currentCounter = counterIdentifier ? 
    counters.find(c => c._id === counterIdentifier) : 
    undefined;
  const isMobile = window.innerWidth < 768;
  
  // Helper function to properly dismiss Bootstrap modals
  // This addresses an issue where modal backdrops remain after closing
  const dismissModal = (modalId: string) => {
    const modalElement = document.getElementById(modalId);
    const modalInstance = Modal.getInstance(modalElement!);
    
    // First hide the modal using Bootstrap's API
    modalInstance?.hide();
    
    // Then clean up the DOM by removing modal-related classes and elements
    // This ensures the UI returns to a fully interactive state
    document.body.classList.remove('modal-open');
    const backdrops = document.getElementsByClassName('modal-backdrop');
    if (backdrops.length) {
      document.body.removeChild(backdrops[0]);
    }
  };
  
  // Memoize expensive calculations to prevent re-renders
  const isExpired = React.useMemo(() => {
    if (!currentCounter || !currentCounter.timeLimitType) return false;
    
    const now = new Date();
    
    if (currentCounter.timeLimitType === 'deadline' && currentCounter.deadline) {
      const deadlineDate = new Date(currentCounter.deadline);
      return now > deadlineDate;
    }
    
    if (currentCounter.timeLimitType === 'countdown' && 
        currentCounter.countdownStartedAt && 
        currentCounter.countdownMinutes) {
      const startedAt = new Date(currentCounter.countdownStartedAt);
      const endTime = new Date(startedAt.getTime() + (currentCounter.countdownMinutes * 60 * 1000));
      return now > endTime;
    }
    
    return false;
  }, [currentCounter?.timeLimitType, currentCounter?.deadline, 
      currentCounter?.countdownStartedAt, currentCounter?.countdownMinutes, timeUpdateTrigger]);
  
  // Memoize the remaining time text calculation
  const remainingTimeText = React.useMemo(() => {
    if (!currentCounter || !currentCounter.timeLimitType) return '';
    
    const now = new Date();
    
    if (currentCounter.timeLimitType === 'deadline' && currentCounter.deadline) {
      const deadlineDate = new Date(currentCounter.deadline);
      if (now > deadlineDate) return t('counters.expired', 'Expired');
      
      const diff = Math.max(0, deadlineDate.getTime() - now.getTime());
      
      // Calculate all time units more accurately
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      
      // For longer time periods, use a more accurate calculation
      const totalDays = Math.floor(diff / (1000 * 60 * 60 * 24));
      const years = Math.floor(totalDays / 365);
      const months = Math.floor((totalDays % 365) / 30);
      const days = Math.floor(totalDays % 30);
      
      if (years > 0) {
        return t('counters.yearsMonthsDaysRemaining', { years, months, days, hours, minutes, seconds });
      } else if (months > 0) {
        return t('counters.monthsDaysRemaining', { months, days, hours, minutes, seconds });
      } else if (days > 0) {
        return t('counters.daysHoursSecondsRemaining', { days, hours, minutes, seconds });
      } else if (hours > 0) {
        return t('counters.hoursMinutesSecondsRemaining', { hours, minutes, seconds });
      } else {
        return t('counters.minutesSecondsRemaining', { minutes, seconds });
      }
    }
    
    if (currentCounter.timeLimitType === 'countdown' && 
        currentCounter.countdownStartedAt && 
        currentCounter.countdownMinutes) {
      const startedAt = new Date(currentCounter.countdownStartedAt);
      const endTime = new Date(startedAt.getTime() + (currentCounter.countdownMinutes * 60 * 1000));
      
      if (now > endTime) return t('counters.expired', 'Expired');
      
      const diff = Math.max(0, endTime.getTime() - now.getTime());
      
      // Calculate all time units more accurately
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      
      // For longer time periods, use a more accurate calculation
      const totalDays = Math.floor(diff / (1000 * 60 * 60 * 24));
      const years = Math.floor(totalDays / 365);
      const months = Math.floor((totalDays % 365) / 30);
      const days = Math.floor(totalDays % 30);
      
      if (years > 0) {
        return t('counters.yearsMonthsDaysRemaining', { years, months, days, hours, minutes, seconds });
      } else if (months > 0) {
        return t('counters.monthsDaysRemaining', { months, days, hours, minutes, seconds });
      } else if (days > 0) {
        return t('counters.daysHoursSecondsRemaining', { days, hours, minutes, seconds });
      } else if (hours > 0) {
        return t('counters.hoursMinutesSecondsRemaining', { hours, minutes, seconds });
      } else if (minutes > 0) {
        return t('counters.minutesSecondsRemaining', { minutes, seconds });
      } else {
        return t('counters.secondsRemaining', { seconds });
      }
    }
    
    return '';
  }, [currentCounter?.timeLimitType, currentCounter?.deadline, 
      currentCounter?.countdownStartedAt, currentCounter?.countdownMinutes, 
      t, timeUpdateTrigger]);
  
  // Memoize the formatted deadline
  const formattedDeadline = React.useMemo(() => {
    if (currentCounter?.timeLimitType === 'deadline' && currentCounter.deadline) {
      return formatDateWithTimezone(currentCounter.deadline, currentCounter.timezone);
    }
    return '';
  }, [currentCounter?.deadline, currentCounter?.timezone, currentCounter?.timeLimitType]);
  
  // All effect hooks
  
  // Set document title
  useDocumentTitle(
    currentCounter ? `${t('counterSettings.title')} - ${currentCounter.name}` : t('counterSettings.title')
  );
  
  // Check if the parameter is an owner URL
  useEffect(() => {
    if (counterId) {
      const counter = findCounterByOwnerUrl(counterId);
      if (counter) {
        setActualCounterId(counter._id);
      } else {
        setActualCounterId(counterId);
      }
    }
  }, [counterId, findCounterByOwnerUrl]);
  
  // Initialize form values when counter data is available
  useEffect(() => {
    if (currentCounter) {
      setCounterName(currentCounter.name);
      setIsPrivate(currentCounter.isPrivate || false);
      
      // Initialize time limit fields
      setTimeLimitType(currentCounter.timeLimitType || null);
      
      // Set deadline if it exists
      if (currentCounter.deadline) {
        const deadlineDate = new Date(currentCounter.deadline);
        setDeadline(normalizeInputDatetime(deadlineDate.toISOString()));
      } else {
        // Default deadline is tomorrow
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(23, 59, 59, 0);
        setDeadline(normalizeInputDatetime(tomorrow.toISOString()));
      }
      
      // Set countdown values if they exist
      setCountdownMinutes(currentCounter.countdownMinutes || 60);
      setCountdownStartedAt(currentCounter.countdownStartedAt || null);
      
      // Set timezone, defaulting to user's timezone if not set
      setTimezone(currentCounter.timezone || getUserTimezone());
    }
  }, [currentCounter]);
  
  // Set the selected counter in context
  useEffect(() => {
    if (actualCounterId && actualCounterId !== selectedCounterId) {
      selectCounter(actualCounterId);
    }
  }, [actualCounterId, selectedCounterId, selectCounter]);
  
  // Add a timer effect for updating the countdown display
  useEffect(() => {
    // Only set up the timer if we have a time-limited counter and aren't in edit mode
    if (currentCounter?.timeLimitType && !isEditing && !isEditingTimeLimit) {
      // Create a second-by-second timer for updating the time display
      // This updates the UI without causing data refreshes
      const timer = setInterval(() => {
        // Check if the counter is expired
        const now = new Date();
        let isCurrentlyExpired = false;
        
        if (currentCounter.timeLimitType === 'deadline' && currentCounter.deadline) {
          const deadlineDate = new Date(currentCounter.deadline);
          isCurrentlyExpired = now > deadlineDate;
        } else if (currentCounter.timeLimitType === 'countdown' && 
                  currentCounter.countdownStartedAt && 
                  currentCounter.countdownMinutes) {
          const startedAt = new Date(currentCounter.countdownStartedAt);
          const endTime = new Date(startedAt.getTime() + (currentCounter.countdownMinutes * 60 * 1000));
          isCurrentlyExpired = now > endTime;
        }
        
        // If the counter just expired (transition from not expired to expired)
        // Refresh the data to update the server state
        if (isCurrentlyExpired && !wasExpired) {
          setWasExpired(true);
          refreshCounters();
        }
        
        // Update trigger to force recalculation of time-related values
        // This doesn't cause a full data refresh, only updates the display
        setTimeUpdateTrigger(prev => prev + 1);
      }, 1000); // Update every second
      
      // Clean up the timer when component unmounts or dependencies change
      return () => clearInterval(timer);
    }
  }, [currentCounter?.timeLimitType, currentCounter?.deadline, 
      currentCounter?.countdownStartedAt, currentCounter?.countdownMinutes,
      isEditing, isEditingTimeLimit, wasExpired, refreshCounters]);
  
  // Clean up when component unmounts or when navigating away
  useEffect(() => {
    return () => {
      // Cancel any pending state updates or API calls
      setLoading(false);
    };
  }, []);
  
  // Don't make any redirect decisions while counters are still loading
  if (countersLoading) {
    return <LoadingSpinner size="lg" />;
  }
  
  // Early returns for redirects
  if (!hasManagementRights) {
    return <Navigate to={`/${counterId}`} replace />;
  }
  
  if (!counterIdentifier) {
    return <Navigate to="/counters" replace />;
  }

  // All handler functions below
  const handleReset = () => {
    const modal = new Modal(document.getElementById('resetModal')!);
    modal.show();
  };

  const confirmReset = async () => {
    try {
      setLoading(true);
      await resetButtonCounts(counterIdentifier, isOwner);
      await refreshCounters();
      
      // Properly hide and dispose the modal
      dismissModal('resetModal');
      
      // Show toast after modal is fully dismissed
      showToast(t('toasts.countsReset'), 'success');
    } catch (err) {
      console.error('Error resetting button counts:', err);
      showToast(t('toasts.resetFailed'), 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = () => {
    const modal = new Modal(document.getElementById('deleteAllModal')!);
    modal.show();
  };

  const confirmDeleteAll = async () => {
    try {
      setLoading(true);
      await deleteAllButtons(counterIdentifier, isOwner);
      await refreshCounters();
      
      // Properly hide and dispose the modal
      dismissModal('deleteAllModal');
      
      // Show toast after modal is fully dismissed
      showToast(t('toasts.allButtonsDeleted'), 'success');
    } catch (err) {
      console.error('Error deleting all buttons:', err);
      showToast(t('toasts.deleteAllFailed'), 'danger');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateCounter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!counterName.trim()) {
      showToast(t('toasts.counterNameRequired', 'Counter name is required'), 'warning');
      return;
    }
    
    if (!actualCounterId) return;
    
    try {
      setLoading(true);
      await updateCounterDetails(actualCounterId, counterName, isPrivate);
      await refreshCounters();
      setIsEditing(false);
      showToast(t('toasts.counterUpdated', 'Counter details updated'), 'success');
    } catch (err) {
      console.error('Error updating counter:', err);
      showToast(t('toasts.updateFailed', 'Failed to update counter'), 'danger');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateTimeLimit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!actualCounterId) return;
    
    // Add validation for countdown minutes
    if (timeLimitType === 'countdown' && !countdownMinutes) {
      showToast(t('toasts.countdownMinutesRequired', 'Countdown duration is required'), 'warning');
      return;
    }
    
    try {
      setLoading(true);
      
      // Format time limit data based on selected type
      let deadlineValue: string | null = null;
      let timezoneValue: string | null = null;
      let countdownMinutesValue: number | null = null;
      let countdownStartedAtValue: string | null = null;
      
      if (timeLimitType === 'deadline') {
        // Use the normalizeInputDatetime function to ensure consistent timezone handling
        deadlineValue = normalizeInputDatetime(deadline);
        timezoneValue = timezone; // Send user's timezone with deadline
      } else if (timeLimitType === 'countdown') {
        // Ensure we have a valid number for countdown minutes
        countdownMinutesValue = countdownMinutes || null;
        // Always start the countdown from now when updating countdown settings
        countdownStartedAtValue = new Date().toISOString();
      }
      
      await updateCounterDetails(
        actualCounterId, 
        counterName, 
        isPrivate, 
        timeLimitType,
        deadlineValue,
        timezoneValue,
        countdownMinutesValue,
        countdownStartedAtValue
      );
      
      await refreshCounters();
      setIsEditingTimeLimit(false);
      showToast(t('toasts.timeLimitUpdated', 'Time limit settings updated'), 'success');
    } catch (err) {
      console.error('Error updating time limit settings:', err);
      showToast(t('toasts.updateFailed', 'Failed to update time limit settings'), 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCounter = () => {
    const modal = new Modal(document.getElementById('deleteCounterModal')!);
    modal.show();
  };

  const confirmDeleteCounter = async () => {
    try {
      setLoading(true);
      
      // Properly hide and dispose the modal
      dismissModal('deleteCounterModal');
      
      // Determine deletion parameters based on access method
      if (isOwner) {
        // Using owner URL
        await removeCounter(counterId!, true);
      } else {
        // Using regular ID
        await removeCounter(actualCounterId!, false);
      }
      
      showToast(t('toasts.counterDeleted', 'Counter deleted successfully'), 'success');
      
      // Navigate immediately to prevent API calls for deleted counter
      navigate('/counter-deleted', { replace: true });
    } catch (err) {
      console.error('Error deleting counter:', err);
      showToast(t('toasts.deleteCounterFailed', 'Failed to delete counter'), 'danger');
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate remaining time for display
  // This implementation is intentionally similar to the one in Counter.tsx
  // to maintain consistent behavior and display format across the application
  // Now using memoized value (remainingTimeText) instead of calculating on every render
  
  // Format deadline for display with timezone info
  // Now using memoized value (formattedDeadline) instead of calculating on every render

  const getTimeLimitNotification = () => {
    if (!currentCounter?.timeLimitType) return null;
    
    const expired = isExpired;
    const remainingTime = remainingTimeText;
    
    if (expired) {
      return (
        <div className="alert alert-warning mb-4">
          <div className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill text-danger fs-4 me-3"></i>
            <div>
              <h6 className="mb-1">{t('counters.expired', 'Expired')}</h6>
              <p className="mb-0 small">
                {t('counters.expiredMessage', 'This counter has expired and can no longer be clicked. You can still view its history and stats.')}
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="alert alert-info mb-4">
        <div className="d-flex align-items-center">
          <i className="bi bi-clock fs-4 me-3"></i>
          <div>
            <h6 className="mb-1">{t('counters.timeRemaining', 'Time Remaining')}</h6>
            <p className="mb-0 small">
              {remainingTime}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container py-3 py-md-5">
      {loading && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-body bg-opacity-75 overlay-bg">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      )}
      
      <div className="row">
        <div className="col-lg-8 mx-auto">
          <div className="text-center mb-3 mb-md-4">
            <h1 className="h3 h1-md fw-bold mb-0">
              {currentCounter?.name 
                ? t('counterSettings.titleWithName', {counterName: currentCounter.name}) 
                : t('counterSettings.title')}
            </h1>
          </div>
          
          <div className="card shadow-sm mb-4 border-0 bg-body-tertiary">
            <div className="card-header bg-body d-flex justify-content-between align-items-center py-3 border-bottom border-opacity-10">
              <h5 className="card-title mb-0 fs-6">{t('counters.name')}</h5>
              <button 
                className="btn btn-sm btn-outline-primary" 
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? (
                  <><i className="bi bi-x-circle me-1"></i>{t('common.cancel')}</>
                ) : (
                  <><i className="bi bi-pencil me-1"></i>{t('common.edit')}</>
                )}
              </button>
            </div>
            <div className="card-body">
              {isEditing ? (
                <form onSubmit={handleUpdateCounter}>
                  <div className="mb-3">
                    <label htmlFor="counterName" className="form-label">
                      {t('counters.name')}*
                    </label>
                    <input 
                      type="text" 
                      className="form-control bg-body-subtle border border-opacity-25" 
                      id="counterName" 
                      value={counterName}
                      onChange={(e) => setCounterName(e.target.value)}
                      required
                    />
                  </div>
                  
                  {(isAdmin || isOwner) && (
                    <div className="card bg-body-tertiary border border-opacity-10 mb-3">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1">{t('counters.visibility')}</h6>
                            <p className="text-body-secondary mb-0 small">
                              {t('counters.privateCounterHint')}
                            </p>
                          </div>
                          <div className="form-check form-switch">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="privacyToggle"
                              checked={!isPrivate}
                              onChange={(e) => {
                                const newPrivacyValue = !e.target.checked;
                                setIsPrivate(newPrivacyValue);
                              }}
                            />
                            <label className="form-check-label" htmlFor="privacyToggle">
                              {isPrivate 
                                ? <span className="badge bg-secondary"><i className="bi bi-eye-slash me-1"></i>{t('counters.private')}</span>
                                : <span className="badge bg-success"><i className="bi bi-eye me-1"></i>{t('counters.public')}</span>
                              }
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <button type="submit" className="btn btn-primary">
                    <i className="bi bi-save me-1"></i>
                    {t('common.save', 'Save')}
                  </button>
                </form>
              ) : (
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="fw-bold mb-0">
                      {counterName}
                    </h5>
                  </div>
                  {(isAdmin || isOwner) && (
                    <div>
                      {isPrivate 
                        ? <span className="badge bg-secondary p-2"><i className="bi bi-eye-slash-fill me-1"></i>{t('counters.private')}</span>
                        : <span className="badge bg-success p-2"><i className="bi bi-eye-fill me-1"></i>{t('counters.public')}</span>
                      }
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Time Limit Card */}
          <div className="card shadow-sm mb-4 border-0 bg-body-tertiary">
            <div className="card-header bg-body d-flex justify-content-between align-items-center py-3 border-bottom border-opacity-10">
              <h5 className="card-title mb-0 fs-6">
                {t('admin.timeLimit', 'Time Limit')}
              </h5>
              <button 
                className="btn btn-sm btn-outline-primary" 
                onClick={() => setIsEditingTimeLimit(!isEditingTimeLimit)}
              >
                {isEditingTimeLimit ? (
                  <><i className="bi bi-x-circle me-1"></i>{t('common.cancel')}</>
                ) : (
                  <><i className="bi bi-pencil me-1"></i>{t('common.edit')}</>
                )}
              </button>
            </div>
            
            {/* Time limit notification moved outside editable area */}
            {currentCounter?.timeLimitType && !isEditingTimeLimit && getTimeLimitNotification()}
            
            <div className="card-body">
              {isEditingTimeLimit ? (
                <form onSubmit={handleUpdateTimeLimit}>
                  <div className="mb-3">
                    <label className="form-label">{t('admin.timeLimitType', 'Time Limit Type')}</label>
                    <div className="d-flex gap-3 mb-3">
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="noTimeLimit"
                          name="timeLimitType"
                          checked={timeLimitType === null}
                          onChange={() => setTimeLimitType(null)}
                        />
                        <label className="form-check-label" htmlFor="noTimeLimit">
                          {t('admin.noTimeLimit', 'No Time Limit')}
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="deadlineTimeLimit"
                          name="timeLimitType"
                          checked={timeLimitType === 'deadline'}
                          onChange={() => setTimeLimitType('deadline')}
                        />
                        <label className="form-check-label" htmlFor="deadlineTimeLimit">
                          {t('admin.deadline', 'Deadline')}
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="countdownTimeLimit"
                          name="timeLimitType"
                          checked={timeLimitType === 'countdown'}
                          onChange={() => setTimeLimitType('countdown')}
                        />
                        <label className="form-check-label" htmlFor="countdownTimeLimit">
                          {t('admin.countdown', 'Countdown')}
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {timeLimitType === 'deadline' && (
                    <div className="mb-3">
                      <label htmlFor="deadlineInput" className="form-label">
                        {t('admin.setDeadline', 'Set Deadline')}
                      </label>
                      <input
                        type="datetime-local"
                        className="form-control bg-body-subtle border border-opacity-25"
                        id="deadlineInput"
                        value={deadline}
                        onChange={(e) => setDeadline(e.target.value)}
                        min={new Date().toISOString().slice(0, 16)}
                        required={timeLimitType === 'deadline'}
                      />
                      <div className="form-text">
                        {t('admin.deadlineHint', 'After this date and time, the counter will no longer be clickable.')}
                        <br />
                        {t('admin.timezoneNote', 'Note: Deadline will be set in your current timezone')}: {timezone}
                      </div>
                    </div>
                  )}
                  
                  {timeLimitType === 'countdown' && (
                    <div className="mb-3">
                      <label htmlFor="countdownInput" className="form-label">
                        {t('admin.countdownDuration', 'Countdown Duration (minutes)')}
                      </label>
                      <input
                        type="number"
                        className="form-control bg-body-subtle border border-opacity-25"
                        id="countdownInput"
                        value={countdownMinutes || ''}
                        onChange={(e) => {
                          // Handle empty input by setting null/undefined temporarily
                          // This prevents the "05" issue when typing after clearing the field
                          const value = e.target.value === '' ? undefined : Number(e.target.value);
                          setCountdownMinutes(value as number);
                        }}
                        min="1"
                        required={timeLimitType === 'countdown'}
                      />
                      <div className="form-text">
                        {t('admin.countdownHint', 'The countdown will start when you save these settings if not already started.')}
                      </div>
                      
                      {countdownStartedAt && (
                        <div className="alert alert-warning mt-2">
                          <small>
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            {t('admin.countdownRestartWarning', 'Warning: Saving will restart the countdown with the new duration.')}
                          </small>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <button type="submit" className="btn btn-primary">
                    <i className="bi bi-save me-1"></i>
                    {t('common.save', 'Save')}
                  </button>
                </form>
              ) : (
                <div>
                  {!currentCounter?.timeLimitType ? (
                    <p className="mb-0">
                      {t('admin.noTimeLimitSet', 'No time limit set. This counter is always available.')}
                    </p>
                  ) : (
                    <div>
                      <div className="mb-3">
                        {currentCounter.timeLimitType === 'deadline' ? (
                          <div className="mb-2">
                            <h6 className="mb-1">{t('admin.deadlineSet', 'Deadline')}</h6>
                            <p className="mb-0">{formattedDeadline}</p>
                            <p className="mb-0 small text-muted">
                              {currentCounter.isExpired ? (
                                <span className="text-danger">{t('counters.expired', 'Expired')}</span>
                              ) : (
                                t('counters.active', 'Active')
                              )}
                            </p>
                          </div>
                        ) : (
                          <div className="mb-2">
                            <h6 className="mb-1">{t('admin.countdownSet', 'Countdown')}</h6>
                            <p className="mb-0">{currentCounter.countdownMinutes} {t('admin.minutes', 'minutes')}</p>
                            {!currentCounter.countdownStartedAt && (
                              <p className="mb-0 small text-warning">
                                <i className="bi bi-hourglass me-1"></i>
                                {t('admin.notStarted', 'Not Started')}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6">
              <div className="card h-100 shadow-sm border-0 bg-body-tertiary">
                <div className="card-header bg-body py-2 border-bottom border-opacity-10">
                  <h5 className="card-title mb-0 fs-6">{t('admin.resetCounts')}</h5>
                </div>
                <div className="card-body d-flex flex-column p-3">
                  <p className="card-text flex-grow-1 small">{t('admin.resetCountsDescription')}</p>
                  <button 
                    className="btn btn-warning btn-sm w-100 w-md-auto align-self-start mt-2"
                    onClick={handleReset}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-counterclockwise me-1"></i>
                    {t('admin.resetCounts')}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="col-12 col-md-6">
              <div className="card h-100 shadow-sm border-0 bg-body-tertiary">
                <div className="card-header bg-body py-2 border-bottom border-opacity-10">
                  <h5 className="card-title mb-0 fs-6">{t('admin.deleteAllButtons')}</h5>
                </div>
                <div className="card-body d-flex flex-column p-3">
                  <p className="card-text flex-grow-1 small">{t('admin.deleteAllButtonsDescription')}</p>
                  <button 
                    className="btn btn-outline-danger btn-sm w-100 w-md-auto align-self-start mt-2"
                    onClick={handleDeleteAll}
                    disabled={loading}
                  >
                    <i className="bi bi-trash me-1"></i>
                    {t('admin.deleteAllButtons')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm mb-4 border border-danger-subtle bg-body-tertiary">
            <div className="card-header bg-danger bg-opacity-10 py-2 border-bottom border-danger-subtle">
              <h5 className="card-title mb-0 fs-6 text-danger">{t('admin.deleteCounter', 'Delete Counter')}</h5>
            </div>
            <div className="card-body d-flex flex-column p-3">
              <p className="card-text flex-grow-1 small">{t('admin.deleteCounterDescription', 'Permanently delete this counter and all its data, including buttons and click history.')}</p>
              <div className="alert alert-danger py-2 px-3 d-flex align-items-center mb-3">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <div className="small">{t('admin.deleteCounterWarning', 'This action cannot be undone!')}</div>
              </div>
              <button 
                className="btn btn-outline-danger btn-sm w-100 w-md-auto align-self-start mt-2"
                onClick={handleDeleteCounter}
                disabled={loading}
              >
                <i className="bi bi-trash-fill me-1"></i>
                {t('admin.deleteCounter', 'Delete Counter')}
              </button>
            </div>
          </div>

          {/* Modals */}
          <div className="modal fade" id="resetModal" tabIndex={-1} aria-labelledby="resetModalLabel" aria-hidden="true">
            <div className="modal-dialog">
              <div className="modal-content bg-body border-0">
                <div className="modal-header border-bottom border-opacity-10">
                  <h5 className="modal-title" id="resetModalLabel">{t('admin.resetCounts')}</h5>
                  <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label={t('common.close', 'Close')}></button>
                </div>
                <div className="modal-body">
                  <p className="small">{t('admin.resetCountsConfirmation')}</p>
                  <div className="alert alert-warning py-2 px-3 d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    <div className="small">{t('admin.resetClearHistoryWarning', 'This will also clear all click history for this counter.')}</div>
                  </div>
                </div>
                <div className="modal-footer border-top border-opacity-10">
                  <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                    {t('common.cancel')}
                  </button>
                  <button type="button" className="btn btn-warning" onClick={confirmReset}>
                    {t('admin.confirmReset')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal fade" id="deleteAllModal" tabIndex={-1} aria-labelledby="deleteAllModalLabel" aria-hidden="true">
            <div className="modal-dialog">
              <div className="modal-content bg-body border-0">
                <div className="modal-header border-bottom border-opacity-10">
                  <h5 className="modal-title" id="deleteAllModalLabel">{t('admin.confirmDeleteAll')}</h5>
                  <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label={t('common.close', 'Close')}></button>
                </div>
                <div className="modal-body">
                  <p className="small">{t('admin.confirmDeleteAllMessage')}</p>
                </div>
                <div className="modal-footer border-top border-opacity-10">
                  <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                    {t('common.cancel')}
                  </button>
                  <button type="button" className="btn btn-danger" onClick={confirmDeleteAll}>
                    {t('common.deleteButtons')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal fade" id="deleteCounterModal" tabIndex={-1} aria-labelledby="deleteCounterModalLabel" aria-hidden="true">
            <div className="modal-dialog">
              <div className="modal-content bg-body border-0">
                <div className="modal-header border-bottom border-opacity-10">
                  <h5 className="modal-title" id="deleteCounterModalLabel">{t('admin.confirmDeleteCounter', 'Delete Counter')}</h5>
                  <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label={t('common.close', 'Close')}></button>
                </div>
                <div className="modal-body">
                  <p className="small">{t('admin.confirmDeleteCounterMessage', 'Are you sure you want to delete this counter?')}</p>
                  <div className="alert alert-danger py-2 px-3 d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    <div className="small">{t('admin.deleteCounterModalWarning', 'This will permanently delete the counter, all its buttons, and all click history. This cannot be undone!')}</div>
                  </div>
                </div>
                <div className="modal-footer border-top border-opacity-10">
                  <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                    {t('common.cancel')}
                  </button>
                  <button type="button" className="btn btn-danger" onClick={confirmDeleteCounter}>
                    {t('common.deleteCounter')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} title={t('counterSettings.title')} />
    </div>
  );
}

export default CounterSettings; 